import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  GlobalStyles,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";

import { viewportCappedHeightSx } from "config";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CustomScrollbars from "../CustomScrollbars";
import { HighlightedText } from "../preview/searchUtils";
import BotsRecordTable, { type BotsColumn } from "./BotsRecordTable";
import CountChip from "./CountChip";
import { formatFileSize } from "./ssnFile";
import {
  EMPTY_ROSTER_MESSAGE,
  MISSING_ROSTER_MESSAGE,
  POSITIONAL_ROSTER_MESSAGE,
  ROSTER_FILE_FILTERS,
  ROSTER_FLAG_LABELS,
  ROSTER_FLAG_TOOLTIPS,
  ROSTER_LOGGED_IDENTITY_HINT,
  ROSTER_ROW_HINT,
  ROSTER_STATE_LABELS,
  RosterFileRecordsResponse,
  RosterFileRowDto,
  RosterFileState,
} from "./rosterFile";
import { useLazyGetJobRosterRecordsQuery } from "../../features/adminUsers/adminUsers";

const PAGE_SIZE = 300;

const SURFACE_SX = {
  bgcolor: "#ffffff",
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  display: "flex",
  overflow: "hidden",
};

const searchMarkStyles = (
  <GlobalStyles
    styles={{
      "mark.preview-search-mark": {
        backgroundColor: "#ffe066",
        color: "inherit",
        padding: "0 1px",
        borderRadius: 2,
      },
    }}
  />
);

const STATE_COLORS: Record<string, "success" | "error" | "default"> = {
  done: "success",
  error: "error",
  not_run: "default",
};

function StateChip({ row }: { row: RosterFileRowDto }) {
  const ambiguous = row.flags.includes("ambiguous");
  const label = ambiguous
    ? ROSTER_FLAG_LABELS.ambiguous
    : ROSTER_STATE_LABELS[row.state];
  const tooltip = ambiguous
    ? ROSTER_FLAG_TOOLTIPS.ambiguous
    : row.resolved
      ? "A failure a later run closed"
      : "";
  const chip = (
    <Chip
      size="small"
      variant="outlined"
      color={ambiguous ? "warning" : STATE_COLORS[row.state]}
      label={row.resolved && !ambiguous ? `${label} (resolved)` : label}
      sx={{ height: 22, fontSize: "0.7rem" }}
    />
  );
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
}

function Detail({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {`${label}: `}
      <Box component="span" sx={{ color: "text.primary" }}>
        {value}
      </Box>
    </Typography>
  );
}

function RosterRowDetails({ row }: { row: RosterFileRowDto }) {
  const fields = Object.entries(row.fields);
  return (
    <Stack spacing={1} sx={{ py: 0.5 }}>
      {row.flags.length ? (
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
          {row.flags.map((flag) => (
            <Tooltip key={flag} title={ROSTER_FLAG_TOOLTIPS[flag]}>
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label={ROSTER_FLAG_LABELS[flag]}
                sx={{ height: 22, fontSize: "0.7rem" }}
              />
            </Tooltip>
          ))}
        </Stack>
      ) : null}
      <Stack
        direction="row"
        sx={{ flexWrap: "wrap", columnGap: 2, rowGap: 0.25 }}
      >
        <Detail label="Last attempt" value={row.last_attempt_at} />
        <Detail label="First failed" value={row.first_failed_at} />
        <Detail label="Level" value={row.level} />
        <Detail label="Portal status" value={row.employee_status} />
        {row.manual ? (
          <Detail label="Closed" value="manually by an operator" />
        ) : null}
        {row.from_deleted_task ? (
          <Detail label="Written by" value="a task that was deleted" />
        ) : null}
      </Stack>
      {fields.length ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
            columnGap: 1.5,
            rowGap: 0.25,
          }}
        >
          {fields.map(([key, value]) => (
            <Fragment key={key}>
              <Typography variant="caption" sx={{ color: "text.disabled" }}>
                {key}
              </Typography>
              <Typography
                variant="caption"
                sx={{ wordBreak: "break-word", fontFamily: "monospace" }}
              >
                {value || "-"}
              </Typography>
            </Fragment>
          ))}
        </Box>
      ) : (
        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          The file holds nothing else about this row.
        </Typography>
      )}
    </Stack>
  );
}

/**
 * The roster of a job, row by row, with what its logs say happened to each.
 *
 * A row's state is folded out of the logs by the employee it names, so a row
 * here and the same employee in the Logs viewer cannot disagree.
 */
export default function RosterFileView({
  jobId,
  heightSx = viewportCappedHeightSx(200, 480, 400),
  onDownload,
}: {
  jobId: number;
  heightSx?: SxProps<Theme>;
  onDownload?: () => void;
}) {
  const [fetchRecords] = useLazyGetJobRosterRecordsQuery();

  const [state, setState] = useState<RosterFileState>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [reveal, setReveal] = useState(false);
  const [records, setRecords] = useState<RosterFileRowDto[]>([]);
  const [meta, setMeta] = useState<RosterFileRecordsResponse | null>(null);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(
    async (mode: "reset" | "more") => {
      if (mode === "more") setLoadingMore(true);
      else setLoading(true);
      try {
        const answer = await fetchRecords(
          {
            jobId,
            state,
            q: search || undefined,
            limit: PAGE_SIZE,
            offset: mode === "more" ? loadedRef.current : 0,
            reveal,
          },
          false,
        ).unwrap();
        setMeta(answer);
        setError(null);
        setRecords((prev) => {
          const next =
            mode === "more" ? [...prev, ...answer.records] : answer.records;
          loadedRef.current = next.length;
          return next;
        });
      } catch (e) {
        const message =
          (e as { data?: { message?: string } })?.data?.message ??
          "Failed to read the roster.";
        setError(message);
        if (mode === "reset") {
          setRecords([]);
          setMeta(null);
          loadedRef.current = 0;
        }
      } finally {
        if (mode === "more") setLoadingMore(false);
        else setLoading(false);
      }
    },
    [fetchRecords, jobId, reveal, search, state],
  );

  useEffect(() => {
    loadedRef.current = 0;
    setExpanded([]);
    void load("reset");
  }, [load]);

  const counts = meta?.counts;
  const file = meta?.file;
  // The Employee column falls back to the folder (an upload roster names
  // nobody else), so a Folder column only earns its place beside a name.
  const showFolder = records.some((row) => row.s3_name && row.employee_name);

  const columns = useMemo<BotsColumn<RosterFileRowDto>[]>(() => {
    const all: (BotsColumn<RosterFileRowDto> | null)[] = [
      {
        key: "index",
        header: (
          <Tooltip title={ROSTER_ROW_HINT}>
            <span style={{ cursor: "help" }}>Row</span>
          </Tooltip>
        ),
        width: 70,
        card: "field",
        cardLabel: "Row",
        cell: (row) => (
          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
            <HighlightedText text={`#${row.index}`} query={search} />
          </Typography>
        ),
      },
      {
        key: "employee",
        header: "Employee",
        card: "title",
        cell: (row) => {
          const shown = row.employee_name || row.s3_name;
          if (shown) {
            return (
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                <HighlightedText text={shown} query={search} />
              </Typography>
            );
          }
          if (row.logged_employee_name) {
            return (
              <Tooltip title={ROSTER_LOGGED_IDENTITY_HINT}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  <HighlightedText text={row.logged_employee_name} query={search} />
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: "text.secondary", ml: 0.75 }}
                  >
                    (from the log)
                  </Typography>
                </Typography>
              </Tooltip>
            );
          }
          return (
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              (the roster does not name this row)
            </Typography>
          );
        },
      },
      {
        key: "employee_id",
        header: "Vendor id",
        width: 150,
        card: "field",
        cardLabel: "Vendor id",
        cell: (row) => {
          if (!row.employee_id && row.logged_employee_id) {
            return (
              <Tooltip title={ROSTER_LOGGED_IDENTITY_HINT}>
                <Typography
                  variant="caption"
                  sx={{ fontFamily: "monospace", color: "text.secondary" }}
                >
                  <HighlightedText text={row.logged_employee_id} query={search} />
                </Typography>
              </Tooltip>
            );
          }
          return (
            <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
              {row.employee_id ? (
                <HighlightedText text={row.employee_id} query={search} />
              ) : (
                "-"
              )}
            </Typography>
          );
        },
      },
      showFolder
        ? {
            key: "s3_name",
            header: "Folder",
            width: 200,
            card: "field",
            cardLabel: "Folder",
            cell: (row) => (
              <Typography variant="caption" sx={{ wordBreak: "break-word" }}>
                {row.s3_name ? (
                  <HighlightedText text={row.s3_name} query={search} />
                ) : (
                  "-"
                )}
              </Typography>
            ),
          }
        : null,
      {
        key: "state",
        header: "State",
        width: 150,
        card: "badge",
        cell: (row) => <StateChip row={row} />,
      },
      {
        key: "task",
        header: "Task",
        width: 100,
        align: "center",
        card: "field",
        cardLabel: "Task",
        cell: (row) => (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {row.task_id != null ? `#${row.task_id}` : "-"}
          </Typography>
        ),
      },
      {
        key: "attempts",
        header: "Attempts",
        width: 100,
        align: "center",
        card: "field",
        cardLabel: "Attempts",
        cell: (row) => (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {row.attempts || "-"}
          </Typography>
        ),
      },
    ];
    return all.filter(Boolean) as BotsColumn<RosterFileRowDto>[];
  }, [search, showFolder]);

  const refresh = () => {
    loadedRef.current = 0;
    void load("reset");
  };

  const missing = file != null && !file.exists;
  const empty = Boolean(file?.exists) && counts?.total === 0;

  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      {searchMarkStyles}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <TextField
          size="small"
          placeholder="Search name, vendor id, folder, #row..."
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          sx={{ minWidth: { xs: "auto", sm: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <ClearIcon
                  fontSize="small"
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSearchInput("")}
                />
              </InputAdornment>
            ) : null,
          }}
        />
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
          {ROSTER_FILE_FILTERS.filter(
            (filter) =>
              filter.value === "all" ||
              (counts?.[filter.value] ?? 0) > 0 ||
              state === filter.value,
          ).map((filter) => (
            <Chip
              key={filter.value}
              size="small"
              label={filter.label}
              onClick={() => setState(filter.value)}
              color={state === filter.value ? "primary" : "default"}
              variant={state === filter.value ? "filled" : "outlined"}
              sx={{ height: 24, fontSize: "0.72rem" }}
            />
          ))}
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        {meta?.can_reveal ? (
          <Tooltip title="A roster row may carry an SSN, an address or an e-mail">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={reveal}
                  onChange={(event) => setReveal(event.target.checked)}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Show full rows
                </Typography>
              }
              sx={{ mr: 0 }}
            />
          </Tooltip>
        ) : null}
        <Button
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          disabled={loading}
          onClick={refresh}
        >
          Refresh
        </Button>
      </Stack>

      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
        <CountChip
          label="Rows"
          value={counts?.total ?? 0}
          tooltip="Rows of the stored file - what the next run would walk"
        />
        <CountChip
          label={ROSTER_STATE_LABELS.done}
          value={counts?.done ?? 0}
          color="success"
          tooltip="Rows a run has finished, matched by the employee they name"
        />
        <CountChip
          label={ROSTER_STATE_LABELS.error}
          value={counts?.error ?? 0}
          color="error"
          tooltip="Rows whose last attempt failed and nothing has fixed since"
        />
        <CountChip
          label={ROSTER_STATE_LABELS.not_run}
          value={counts?.not_run ?? 0}
          tooltip="Rows no log record speaks for - the work still ahead"
        />
        {(counts?.ambiguous ?? 0) > 0 ? (
          <CountChip
            label={ROSTER_FLAG_LABELS.ambiguous}
            value={counts?.ambiguous ?? 0}
            color="warning"
            tooltip={ROSTER_FLAG_TOOLTIPS.ambiguous}
          />
        ) : null}
        {(counts?.duplicate ?? 0) > 0 ? (
          <CountChip
            label={ROSTER_FLAG_LABELS.duplicate}
            value={counts?.duplicate ?? 0}
            color="warning"
            tooltip={ROSTER_FLAG_TOOLTIPS.duplicate}
          />
        ) : null}
        {(counts?.no_identity ?? 0) > 0 ? (
          <CountChip
            label={ROSTER_FLAG_LABELS.no_identity}
            value={counts?.no_identity ?? 0}
            color="warning"
            tooltip={ROSTER_FLAG_TOOLTIPS.no_identity}
          />
        ) : null}
        {(counts?.records_not_in_roster ?? 0) > 0 ? (
          <CountChip
            label="Not in the roster"
            value={counts?.records_not_in_roster ?? 0}
            tooltip="Employees the logs have a record for that this file no longer holds"
          />
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        {file ? (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {[
              file.filename,
              formatFileSize(file.size_bytes),
              file.last_modified
                ? new Date(file.last_modified).toLocaleString()
                : "",
            ]
              .filter(Boolean)
              .join(" - ")}
          </Typography>
        ) : null}
      </Stack>

      {counts && counts.employees_done !== counts.done ? (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {`The logs name ${counts.employees_done} employee(s) done and ` +
            `${counts.employees_error} still failing - the job counts people, this ` +
            "file counts rows, and it lists some of them more than once."}
        </Typography>
      ) : null}

      {meta?.positional ? (
        <Alert severity="warning">{POSITIONAL_ROSTER_MESSAGE}</Alert>
      ) : null}
      {missing && !error ? (
        <Alert severity="info">{MISSING_ROSTER_MESSAGE}</Alert>
      ) : null}
      {empty && !error ? (
        <Alert
          severity="warning"
          action={
            onDownload ? (
              <Button size="small" color="inherit" onClick={onDownload}>
                Download
              </Button>
            ) : undefined
          }
        >
          {EMPTY_ROSTER_MESSAGE}
        </Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      {!missing ? (
        <Box sx={{ ...SURFACE_SX, ...heightSx }}>
          <CustomScrollbars>
            <BotsRecordTable<RosterFileRowDto>
              rows={records}
              columns={columns}
              rowKey={(row) => row.index}
              loading={loading && !records.length}
              skeletonRows={6}
              bordered={false}
              empty={
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {counts?.total ? "No rows match the filters." : "(empty)"}
                </Typography>
              }
              renderExpanded={(row) => <RosterRowDetails row={row} />}
              expandAdornment={(_row, isOpen) => (
                <Box sx={{ color: "text.disabled", display: "flex" }}>
                  {isOpen ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </Box>
              )}
              expandedIds={expanded}
              onToggleExpand={(row) =>
                setExpanded((prev) =>
                  prev.includes(row.index)
                    ? prev.filter((item) => item !== row.index)
                    : [...prev, row.index],
                )
              }
            />
          </CustomScrollbars>
        </Box>
      ) : null}

      {!missing ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {`Showing ${records.length} of ${meta?.total_matched ?? 0}`}
          </Typography>
          {meta?.has_more ? (
            <Button
              size="small"
              disabled={loadingMore}
              startIcon={
                loadingMore ? <CircularProgress size={14} /> : undefined
              }
              onClick={() => void load("more")}
            >
              Load more
            </Button>
          ) : null}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            {meta?.revealed ? "rows shown in full" : "sensitive values masked"}
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
}
